/**
 * Utility for retrying failed dynamic imports.
 * Handles the "Failed to fetch dynamically imported module" error
 * that occurs when a cached index.html references outdated chunk filenames.
 */

const CHUNK_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "Loading chunk",
  "Loading CSS chunk",
  "error loading dynamically imported module",
  "Importing a module script failed",
];

/**
 * Check if an error is a chunk loading error
 */
export function isChunkLoadingError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) =>
    message.toLowerCase().includes(pattern.toLowerCase())
  );
}

/**
 * Wrap a dynamic import with retry logic.
 * On failure, refreshes the page to get the latest chunk references.
 */
export function lazyWithRetry<T extends React.ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>
): () => Promise<{ default: T }> {
  return async () => {
    try {
      return await importFn();
    } catch (error) {
      if (isChunkLoadingError(error)) {
        // Check if we've already tried refreshing
        const refreshKey = "dynamic_import_refresh";
        const lastRefresh = sessionStorage.getItem(refreshKey);
        const now = Date.now();

        // Only refresh if we haven't refreshed in the last 10 seconds
        if (!lastRefresh || now - parseInt(lastRefresh, 10) > 10000) {
          sessionStorage.setItem(refreshKey, now.toString());
          // Force reload to get fresh chunk references
          window.location.reload();
          // Return a promise that never resolves (page is reloading)
          return new Promise(() => {});
        }
        
        // If we already refreshed recently and still failing, 
        // throw a more user-friendly error
        const userError = new Error(
          "Please refresh your browser to load the latest version of this page."
        );
        userError.name = "ChunkLoadError";
        throw userError;
      }

      // Re-throw non-chunk errors
      throw error;
    }
  };
}
